import { ConditionalRenderer } from '@/components/conditional-renderer';
import { useNodeProps } from '@/node-editor/hooks';
import { ITextLink } from '@/types/link';
import { INode, INodeProps } from '@/types/node';
import React, { useContext } from 'react';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { IContent } from './contract';
import { Box, Typography } from '@mui/material';
import { AuthContext } from '@/store';
import { getSpacerValue } from '@/lib/utils/format';
import { MarkdownRender } from '@/components/markdown/markdownRender';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}
interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}
const useStyles = ({
  override,
  theme,
  imageHeight,
}: {
  override: Record<string, any>;
  theme: any;
  imageHeight: number;
}) => ({
  postTitle: {
    lineHeight: '1.2',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontFamily: theme.typography.h1?.fontFamily || theme.typography.fontFamily,
    ...override.postTitle,
  },
  dateTime: {
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: '1.2',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    ...override.dateTime,
  },
  link: {
    display: 'block',
    width: '100%',
    ...override.link,
  },
  image: {
    display: 'block',
    height:
      parseInt(imageHeight.toString()) === 0 ? 'auto' : `${imageHeight}px`,
    width: '100%',
    borderRadius: '8px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    objectFit: 'cover',
    ...override.image,
  },
});

export const Render = ({
  node: {
    id,
    override,
    content: {
      imageHeight = 0,
      src,
      link,
      text,
      alignment,
      verticalSpacing,
      postTitle,
      dateTime,
    },
  },
  conferenceId,
}: IProps) => {
  const theme = useTheme();
  const classes = useStyles({ override, theme, imageHeight });
  const { authState } = useContext(AuthContext);

  const hasLink = (link: ITextLink): boolean =>
    'targetUrl' in link && link.targetUrl !== '';

  const normalizeUrl = (url: string): string => {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    // for local  links
    if (/^\.?\.?\//.test(url)) {
      return url;
    }

    return `https://${url}`;
  };

  const markdownContainerSx = {
    width: '100%',
    textAlign:
      alignment.horizontal === 'flex-start'
        ? 'left'
        : alignment.horizontal === 'flex-end'
        ? 'right'
        : alignment.horizontal,
    paddingTop: theme.spacing(getSpacerValue(verticalSpacing.top)),
    paddingBottom: theme.spacing(getSpacerValue(verticalSpacing.bottom)),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...override?.root,
  };

  return (
    <Box {...useNodeProps(id)} sx={markdownContainerSx}>
      {postTitle && (
        <Typography variant="h4" component="h4" sx={classes.postTitle}>
          {postTitle}
        </Typography>
      )}
      {dateTime && (
        <Typography component="p" sx={classes.dateTime}>
          {moment(dateTime).format('DD MMM YYYY HH:mm')}
        </Typography>
      )}

      <ConditionalRenderer
        condition={hasLink(link)}
        render={(children) => (
          <Box
            component="a"
            sx={classes.link}
            rel={link.targetRel || 'noopener noreferrer'}
            target={link.target}
            href={normalizeUrl(link.targetUrl)}
          >
            {children}
          </Box>
        )}
      >
        <Box
          component="img"
          sx={classes.image}
          src={getImage(src, ImageSize.Large, conferenceId)}
        />
      </ConditionalRenderer>

      {text && <MarkdownRender text={text} templateData={authState.user} />}
    </Box>
  );
};
